/**
 * IoT Shack Agent - AI Assistant for Device Management
 *
 * Runs the canonical Volcano agent (`iot-shack`) workflows via /cp/api/volcano/execute.
 * This replaces the old /agent-tools/* calls (those are not a stable CP workflow surface).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cpFetch, getDefaultTenantId } from '../../lib/cpClient';
import { useAuthStore } from '../../store/authStore';
import { createDevice, fetchModels } from './api';
import {
  describeTenantLabel,
  resolveQueryTenant,
  tenantForRequest,
} from './tenantLabelHonesty';
import {
  CpuChipIcon,
  SignalIcon,
  DevicePhoneMobileIcon,
  DocumentPlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  data?: any;
}

interface AgentTool {
  name: string;
  description: string;
  icon: React.ElementType;
  example: string;
  workflow: string;
}

const agentTools: AgentTool[] = [
  {
    name: 'Onboard Sensor (PIM/DAM)',
    description: 'End-to-end onboarding: templates + PIM product + decoder + device + listener',
    icon: SparklesIcon,
    example:
      '/onboard-sensor {"tenant":"dev","deviceId":"fmt100_001","listenerId":"listener_fmt100_001","env":"prod","port":5000,"source":{"kind":"command","command":"Teltonika FMT100 GPS tracker"}}',
    workflow: 'onboard_sensor_with_pim_dam',
  },
  {
    name: 'Register Device (Sensor)',
    description: 'Create a device record via Volcano workflow',
    icon: DevicePhoneMobileIcon,
    example:
      '/register-device {"tenant":"dev","device_id":"soil_sensor_001","device_type":"soil_sensor","manufacturer":"acme","model":"v1"}',
    workflow: 'register_device',
  },
  {
    name: 'Register Gateway',
    description: 'Create a gateway record via Volcano workflow',
    icon: SignalIcon,
    example:
      '/register-gateway {"tenant":"dev","gatewayId":"gw_001","type":"lora","manufacturer":"acme","model":"gw-v1","serialNumber":"SN123"}',
    workflow: 'register_gateway',
  },
  {
    name: 'Generate Decoder (from examples)',
    description: 'LLM → persist decoder via Volcano workflow',
    icon: CpuChipIcon,
    example:
      '/generate-decoder {"type":"soil_sensor","vendor":"acme","model":"v1","payloadType":"json","examples":[{"temperature":21.4,"deviceId":"soil_sensor_001"}]}',
    workflow: 'generate_decoder_from_examples',
  },
  {
    name: 'Link Device → Gateway',
    description: 'Create gateway_sensors link + update device.gatewayId',
    icon: DocumentPlusIcon,
    example:
      '/link-device {"tenant":"dev","gatewayId":"gw_001","deviceId":"soil_sensor_001","connectionType":"lora","rssi":-80}',
    workflow: 'link_device_to_gateway',
  },
  {
    name: 'Create Listener',
    description: 'Create a listener record (for iot-listener runtime)',
    icon: SparklesIcon,
    example:
      '/create-listener {"tenant":"dev","listenerId":"listener_001","env":"dev","mode":"lorawan_webhook","port":null}',
    workflow: 'create_listener',
  },
  {
    name: 'Create Telemetry Template',
    description: 'Create a Telemetry Sensor template under IKU IoT',
    icon: DocumentPlusIcon,
    example:
      '/create-telemetry-template {"tenant":"dev","sensor_name":"Telemetry Sensor","manufacturer":"Teltonika","model":"FMT100","sensor_type":"telemetry_sensor","sample_payload":{"lat":-33.85,"lng":18.49,"speed":0}}',
    workflow: 'create_telemetry_sensor_template',
  },
];

export default function IoTAgent() {
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const queryTenant = resolveQueryTenant({
    selectedTenant,
    hostedTenant: getDefaultTenantId(),
  });
  const [tenantId, setTenantId] = useState(() => queryTenant || '');
  const tenantLabel = describeTenantLabel({ queryTenant, localTenant: tenantId });

  useEffect(() => {
    if (!tenantId && queryTenant) setTenantId(queryTenant);
  }, [queryTenant, tenantId]);

  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: `👋 **IoT Shack Agent** ready to help!

This page runs **real Volcano workflows** on the \`iot-shack\` agent.

**How to use:**
Send a command + JSON. No guessing; you must provide IDs/types explicitly.

Examples:
${agentTools.map((t) => `- \`${t.example}\``).join('\n')}`,
      timestamp: new Date(),
    },
  ]);

  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Onboarding wizard (URL/PDF/Text)
  const [onboardUrl, setOnboardUrl] = useState('https://www.teltonika-gps.com/products/trackers/fast-easy/fmt100');
  const [onboardText, setOnboardText] = useState('');
  const [onboardPdf, setOnboardPdf] = useState<File | null>(null);
  const [onboardDamAssetId, setOnboardDamAssetId] = useState<string>('');
  const [onboardEnv, setOnboardEnv] = useState<'dev' | 'prod'>('prod');
  const [onboardPort, setOnboardPort] = useState<number>(5000);
  const [sensorName, setSensorName] = useState('Telemetry Sensor');
  const [sensorManufacturer, setSensorManufacturer] = useState('Teltonika');
  const [sensorModel, setSensorModel] = useState('FMT100');
  const [sensorType, setSensorType] = useState('telemetry_sensor');
  const [samplePayload, setSamplePayload] = useState('');
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [attachImei, setAttachImei] = useState('');
  const [attachGatewayId, setAttachGatewayId] = useState('');
  const [showAdvancedOnboard, setShowAdvancedOnboard] = useState(false);

  const modelsQuery = useQuery({
    queryKey: ['iot', 'models'],
    queryFn: () => fetchModels(),
    staleTime: 60_000,
  });
  const models = modelsQuery.data || [];
  const selectedModel = useMemo(
    () => models.find((m) => m.id === selectedModelId || m.slug === selectedModelId) || null,
    [models, selectedModelId]
  );
  const defaultIds = useMemo(() => {
    const suffix = String(Date.now());
    return {
      deviceId: `fmt100_${suffix}`,
      listenerId: `listener_fmt100_${suffix}`,
    };
  }, []);
  const [onboardDeviceId, setOnboardDeviceId] = useState(defaultIds.deviceId);
  const [onboardListenerId, setOnboardListenerId] = useState(defaultIds.listenerId);

  const executeWorkflow = useMutation({
    mutationFn: async ({ workflow, input }: { workflow: string; input: any }) => {
      return cpFetch('/api/volcano/execute', {
        method: 'POST',
        body: JSON.stringify({
          agent_id: 'iot-shack',
          workflow,
          input,
          context: {
            tenant_id: input?.tenant || tenantForRequest(tenantId, queryTenant),
            source: 'farmer-portal',
          },
        }),
      });
    },
    onSuccess: (data, variables) => {
      const tool = agentTools.find((t) => t.workflow === variables.workflow);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `✅ **${tool?.name || variables.workflow} Complete**\n\n${formatVolcanoResponse(data)}`,
          timestamp: new Date(),
          data,
        },
      ]);
      setIsProcessing(false);
    },
    onError: (error: any, variables) => {
      const tool = agentTools.find((t) => t.workflow === variables.workflow);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `❌ **${tool?.name || variables.workflow} Failed**\n\n${error.message || 'Unknown error'}`,
          timestamp: new Date(),
        },
      ]);
      setIsProcessing(false);
    },
  });

  const attachDevice = useMutation({
    mutationFn: async (payload: any) => createDevice(payload),
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `✅ **Device instance created**\n\nIMEI: ${data.deviceId}\nModel: ${selectedModel?.name || 'unknown'}`,
          timestamp: new Date(),
          data,
        },
      ]);
      setAttachImei('');
      setAttachGatewayId('');
    },
    onError: (error: any) => {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `❌ **Attach IMEI failed**\n\n${error?.message || 'Unknown error'}`,
          timestamp: new Date(),
        },
      ]);
    },
  });

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsProcessing(true);

    // Parse a command + JSON payload.
    const intent = detectIntent(input);

    if (!intent) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            `I didn’t run anything because I need an explicit command + JSON payload.\n\n` +
            `Pick one of these and fill in the fields:\n\n` +
            `${agentTools.map((t) => `- \`${t.example}\``).join('\n')}`,
          timestamp: new Date(),
        },
      ]);
      setIsProcessing(false);
      return;
    }

    const { workflow, data } = intent;
    executeWorkflow.mutate({ workflow, input: data });
  };

  const handleUploadPdfToDam = async () => {
    if (!onboardPdf) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', onboardPdf);
      formData.append('compress', 'true');
      const resp: any = await cpFetch('/dam/assets/upload', { method: 'POST', body: formData });
      if (!resp?.success || !resp?.asset_id) throw new Error(resp?.error || 'DAM upload failed');
      setOnboardDamAssetId(String(resp.asset_id));
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `✅ PDF uploaded to DAM\n\nasset_id: ${resp.asset_id}`, timestamp: new Date(), data: resp },
      ]);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `❌ PDF upload failed\n\n${e?.message || 'Unknown error'}`, timestamp: new Date() },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExtractTextFromDam = async () => {
    if (!onboardDamAssetId) return;
    setIsProcessing(true);
    try {
      const resp: any = await cpFetch(`/dam/assets/${encodeURIComponent(onboardDamAssetId)}/extract-text`, {
        method: 'POST',
        body: JSON.stringify({ force: false }),
      });
      if (!resp?.success) throw new Error(resp?.error || 'Extract text failed');
      const extracted = String(resp?.extracted_text || '');
      setOnboardText(extracted);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `✅ Extracted text from DAM\n\nchars: ${extracted.length}\npages: ${typeof resp?.pages === 'number' ? resp.pages : 'unknown'}`,
          timestamp: new Date(),
          data: resp,
        },
      ]);
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `❌ Extract text failed\n\n${e?.message || 'Unknown error'}`, timestamp: new Date() },
      ]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateTelemetryTemplate = async () => {
    if (isProcessing) return;
    let parsedPayload: any = null;
    if (samplePayload.trim()) {
      try {
        parsedPayload = JSON.parse(samplePayload);
      } catch (e: any) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `❌ **Sample payload is not valid JSON**\n\n${e?.message || 'Invalid JSON'}`,
            timestamp: new Date(),
          },
        ]);
        return;
      }
    }
    const payload = {
      tenant: tenantForRequest(tenantId, queryTenant),
      sensor_name: sensorName,
      manufacturer: sensorManufacturer,
      model: sensorModel,
      sensor_type: sensorType,
      sample_payload: parsedPayload,
      source: onboardText.trim()
        ? { kind: 'text', text: onboardText }
        : onboardUrl.trim()
          ? { kind: 'url', url: onboardUrl }
          : null,
    };
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: `/create-telemetry-template ${JSON.stringify(payload)}`, timestamp: new Date() },
    ]);
    setIsProcessing(true);
    executeWorkflow.mutate({ workflow: 'create_telemetry_sensor_template', input: payload });
  };

  const handleAttachImei = async () => {
    if (!attachImei.trim() || attachDevice.isPending) return;
    const model = selectedModel;
    const payload = {
      deviceId: attachImei.trim(),
      type: model?.type || 'telemetry_sensor',
      templateId: model?.deviceTemplateId || null,
      metadata: {
        imei: attachImei.trim(),
        manufacturer: model?.manufacturer || sensorManufacturer || null,
        model: model?.model || sensorModel || null,
        model_product_id: model?.id || null,
        model_id: model?.id || null,
      },
      gatewayId: attachGatewayId.trim() || null,
    };
    attachDevice.mutate(payload);
  };

  const handleOnboard = async () => {
    if (isProcessing) return;
    const hasText = Boolean(onboardText.trim());
    const hasUrl = Boolean(onboardUrl.trim());
    if (!hasText && !hasUrl) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Please provide either a URL or some text (or upload a PDF and extract text).', timestamp: new Date() },
      ]);
      return;
    }

    const payload = {
      tenant: tenantForRequest(tenantId, queryTenant),
      env: onboardEnv,
      port: onboardPort,
      deviceId: onboardDeviceId,
      listenerId: onboardListenerId,
      source: hasText ? { kind: 'text', text: onboardText } : { kind: 'url', url: onboardUrl },
    };

    setMessages((prev) => [
      ...prev,
      { role: 'user', content: `/onboard-device ${JSON.stringify(payload)}`, timestamp: new Date() },
    ]);
    setIsProcessing(true);
    executeWorkflow.mutate({ workflow: 'onboard_sensor_with_pim_dam', input: payload });
  };

  const handleQuickAction = (tool: AgentTool) => {
    setInput(tool.example);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-160px)] bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-500 rounded-lg">
            <SparklesIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">IoT Shack Agent</h2>
            <p className="text-sm text-gray-600">AI-powered device management</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {agentTools.map((tool, idx) => {
            const Icon = tool.icon;
            return (
              <button
                key={idx}
                onClick={() => handleQuickAction(tool)}
                className="p-2 hover:bg-white rounded-lg transition-colors"
                title={tool.name}
              >
                <Icon className="h-5 w-5 text-gray-600" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Sensor setup + Attach IMEI */}
      <div className="p-4 border-b border-gray-200 bg-white space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">Step 1: Sensor Config</div>
            <div className="text-xs text-gray-500">
              Create/update the Telemetry Sensor template from real payloads. No device instances here.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600">Tenant</label>
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded text-sm w-28"
              disabled={isProcessing}
              aria-label="Tenant"
            />
            {tenantLabel.sandboxNote ? (
              <span className="text-xs text-amber-700">{tenantLabel.sandboxNote}</span>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-700">Sensor name</label>
                <input
                  value={sensorName}
                  onChange={(e) => setSensorName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Sensor type</label>
                <input
                  value={sensorType}
                  onChange={(e) => setSensorType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Manufacturer</label>
                <input
                  value={sensorManufacturer}
                  onChange={(e) => setSensorManufacturer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Model</label>
                <input
                  value={sensorModel}
                  onChange={(e) => setSensorModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700">Device docs URL (optional)</label>
              <input
                value={onboardUrl}
                onChange={(e) => setOnboardUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                disabled={isProcessing}
              />
              <label className="text-xs font-medium text-gray-700">Sample payload JSON (preferred)</label>
              <textarea
                value={samplePayload}
                onChange={(e) => setSamplePayload(e.target.value)}
                placeholder='Paste a GET_get_devices payload example here'
                className="w-full h-28 px-3 py-2 border border-gray-300 rounded text-sm"
                disabled={isProcessing}
              />
              <label className="text-xs font-medium text-gray-700">Additional notes (optional)</label>
              <textarea
                value={onboardText}
                onChange={(e) => setOnboardText(e.target.value)}
                placeholder="Notes to help the agent (optional)"
                className="w-full h-20 px-3 py-2 border border-gray-300 rounded text-sm"
                disabled={isProcessing}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
              <div className="text-xs font-semibold text-gray-700 mb-1">PDF (optional)</div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setOnboardPdf(e.target.files?.[0] || null)}
                  disabled={isProcessing}
                />
                <button
                  onClick={handleUploadPdfToDam}
                  disabled={isProcessing || !onboardPdf}
                  className="px-3 py-2 text-sm bg-gray-900 text-white rounded disabled:bg-gray-300"
                >
                  Upload
                </button>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input
                  value={onboardDamAssetId}
                  onChange={(e) => setOnboardDamAssetId(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                  placeholder="DAM asset_id"
                  disabled={isProcessing}
                />
                <button
                  onClick={handleExtractTextFromDam}
                  disabled={isProcessing || !onboardDamAssetId}
                  className="px-3 py-2 text-sm bg-gray-900 text-white rounded disabled:bg-gray-300"
                >
                  Extract
                </button>
              </div>
            </div>

            <button
              onClick={handleCreateTelemetryTemplate}
              disabled={isProcessing || (!samplePayload.trim() && !onboardText.trim() && !onboardUrl.trim())}
              className="w-full px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
            >
              {isProcessing ? 'Processing…' : 'Create/Update Telemetry Template'}
            </button>

            <button
              onClick={() => setShowAdvancedOnboard((v) => !v)}
              className="w-full px-4 py-2 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
            >
              {showAdvancedOnboard ? 'Hide advanced onboarding' : 'Show advanced onboarding'}
            </button>
          </div>
        </div>

        {showAdvancedOnboard && (
          <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 space-y-3">
            <div className="text-xs font-semibold text-gray-700">Advanced: Full onboard (templates + PIM + decoder + device)</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-700">deviceId</label>
                <input
                  value={onboardDeviceId}
                  onChange={(e) => setOnboardDeviceId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">listenerId</label>
                <input
                  value={onboardListenerId}
                  onChange={(e) => setOnboardListenerId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">env</label>
                <select
                  value={onboardEnv}
                  onChange={(e) => setOnboardEnv((e.target.value as any) || 'prod')}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                >
                  <option value="dev">dev</option>
                  <option value="prod">prod</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">port</label>
                <input
                  value={String(onboardPort)}
                  onChange={(e) => setOnboardPort(Number(e.target.value || 0) || 5000)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  disabled={isProcessing}
                />
              </div>
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={handleOnboard}
                disabled={isProcessing || (!onboardText.trim() && !onboardUrl.trim())}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
              >
                {isProcessing ? 'Processing…' : 'Run full onboard'}
              </button>
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-4 space-y-3">
          <div className="text-sm font-semibold text-gray-900">Step 2: Attach IMEI to a sensor config</div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <label className="text-xs font-medium text-gray-700">Select sensor model</label>
              <select
                value={selectedModelId}
                onChange={(e) => setSelectedModelId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                disabled={isProcessing || modelsQuery.isLoading}
              >
                <option value="">Choose model…</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">IMEI</label>
              <input
                value={attachImei}
                onChange={(e) => setAttachImei(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="e.g. 350612071541138"
                disabled={isProcessing}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Gateway ID (optional)</label>
              <input
                value={attachGatewayId}
                onChange={(e) => setAttachGatewayId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                placeholder="gw_001"
                disabled={isProcessing}
              />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <button
              onClick={handleAttachImei}
              disabled={isProcessing || !attachImei.trim() || !selectedModelId}
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-gray-300"
            >
              Attach IMEI
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-white">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-3xl rounded-lg p-4 ${
                msg.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="whitespace-pre-wrap">{msg.content}</div>
              <div className="text-xs mt-2 opacity-70">
                {msg.timestamp.toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        {isProcessing && (
          <div className="flex justify-start">
            <div className="max-w-3xl rounded-lg p-4 bg-gray-100 text-gray-900">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Processing...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-start space-x-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder='Paste a command here. Press Ctrl+Enter to send.'
            className="flex-1 min-h-[90px] px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            disabled={isProcessing}
          />
          <button
            onClick={handleSend}
            disabled={isProcessing || !input.trim()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? 'Processing...' : 'Send'}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {agentTools.map((tool, idx) => (
            <button
              key={idx}
              onClick={() => handleQuickAction(tool)}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-full hover:border-blue-500 hover:text-blue-600 transition-colors"
              disabled={isProcessing}
            >
              {tool.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Simple intent detection (in production, this would use LLM)
 */
function detectIntent(input: string): { workflow: string; data: any } | null {
  const trimmed = String(input || '').trim();
  if (!trimmed) return null;

  // Expected formats:
  //   /onboard-sensor {...json...}
  //   /register-device {...json...}
  //   /register-gateway {...json...}
  //   /generate-decoder {...json...}
  //   /link-device {...json...}
  //   /create-listener {...json...}
  const m = trimmed.match(/^\/?(onboard-device|onboard-sensor|register-device|register-gateway|generate-decoder|link-device|create-listener|create-telemetry-template)\s+([\s\S]+)$/i);
  if (!m) return null;

  const cmd = String(m[1]).toLowerCase();
  const jsonPart = String(m[2]).trim();
  if (!jsonPart.startsWith('{') && !jsonPart.startsWith('[')) return null;

  try {
    const parsed = JSON.parse(jsonPart);
    if (cmd === 'onboard-device' || cmd === 'onboard-sensor') {
      return { workflow: 'onboard_sensor_with_pim_dam', data: parsed };
    }
    if (cmd === 'register-device') return { workflow: 'register_device', data: parsed };
    if (cmd === 'register-gateway') return { workflow: 'register_gateway', data: parsed };
    if (cmd === 'generate-decoder') return { workflow: 'generate_decoder_from_examples', data: parsed };
    if (cmd === 'link-device') return { workflow: 'link_device_to_gateway', data: parsed };
    if (cmd === 'create-listener') return { workflow: 'create_listener', data: parsed };
    if (cmd === 'create-telemetry-template') return { workflow: 'create_telemetry_sensor_template', data: parsed };
    return null;
  } catch {
    return null;
  }
}

/**
 * Format Volcano response for display
 */
function formatVolcanoResponse(data: any): string {
  if (!data) return 'No response';
  const success = typeof data.success === 'boolean' ? data.success : null;
  const internal = data.internal_data || data.output?.internal_data || data.output || null;
  const summary = success === null ? 'Completed.' : success ? 'Success.' : 'Failed.';
  if (internal && typeof internal === 'object') {
    return `${summary}\n\ninternal_data:\n${JSON.stringify(internal, null, 2)}`;
  }
  return `${summary}\n\nraw:\n${JSON.stringify(data, null, 2)}`;
}

